import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type QuoteAccountMode = 'register' | 'login';

interface QuoteAccountPanelProps {
  isAuthenticated: boolean;
  sessionEmail?: string;
  mode: QuoteAccountMode;
  onModeChange: (mode: QuoteAccountMode) => void;
  password: string;
  onPasswordChange: (value: string) => void;
  passwordConfirmation: string;
  onPasswordConfirmationChange: (value: string) => void;
  error?: string | null;
  tone?: 'dark' | 'light';
}

export default function QuoteAccountPanel({
  isAuthenticated,
  sessionEmail,
  mode,
  onModeChange,
  password,
  onPasswordChange,
  passwordConfirmation,
  onPasswordConfirmationChange,
  error,
  tone = 'light',
}: QuoteAccountPanelProps) {
  const isLight = tone === 'light';
  const inputClass = isLight
    ? 'bg-white border-[rgba(17,17,17,0.14)] text-[#111111] h-12'
    : 'bg-[#0d1b2e] border-white/10 text-white h-12';
  const labelClass = isLight ? 'text-[#333333] text-sm mb-2 block' : 'text-white/70 text-sm mb-2 block';

  return (
    <div
      className={`mt-6 rounded-xl border p-5 ${
        isLight
          ? 'border-[rgba(17,17,17,0.08)] bg-white'
          : 'border-white/10 bg-[#0d1b2e]'
      }`}
    >
      <h4 className={`text-sm font-semibold mb-1 ${isLight ? 'text-[#111111]' : 'text-white'}`}>
        Portal access
      </h4>

      {isAuthenticated && sessionEmail ? (
        <p className={`text-sm ${isLight ? 'text-[#666666]' : 'text-white/50'}`}>
          Signed in as{' '}
          <span className={`font-medium ${isLight ? 'text-[#111111]' : 'text-white'}`}>
            {sessionEmail}
          </span>
          . Your quote will be linked to this account.
        </p>
      ) : (
        <>
          <p className={`text-sm mb-4 ${isLight ? 'text-[#666666]' : 'text-white/50'}`}>
            Create an account to track your project in the portal.
          </p>

          <div
            className={`flex rounded-lg border p-1 gap-1 mb-4 ${
              isLight
                ? 'border-[rgba(17,17,17,0.08)] bg-[#EFEDE8]'
                : 'border-white/10 bg-[#050d1a]'
            }`}
          >
            <button
              type="button"
              onClick={() => onModeChange('register')}
              className={`flex-1 rounded-md px-3 py-2 text-xs font-semibold transition-all ${
                mode === 'register'
                  ? 'bg-[#D4FF00] text-[#0E2A22]'
                  : isLight
                    ? 'text-[#666666] hover:text-[#111111]'
                    : 'text-white/50 hover:text-white'
              }`}
            >
              Create account
            </button>
            <button
              type="button"
              onClick={() => onModeChange('login')}
              className={`flex-1 rounded-md px-3 py-2 text-xs font-semibold transition-all ${
                mode === 'login'
                  ? 'bg-[#D4FF00] text-[#0E2A22]'
                  : isLight
                    ? 'text-[#666666] hover:text-[#111111]'
                    : 'text-white/50 hover:text-white'
              }`}
            >
              Sign in
            </button>
          </div>

          <p className={`text-xs mb-4 ${isLight ? 'text-[#666666]' : 'text-white/40'}`}>
            {mode === 'login'
              ? 'Use the email above and your existing password.'
              : 'Choose a password for your client account.'}
          </p>

          {error && (
            <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label className={labelClass}>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => onPasswordChange(e.target.value)}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                className={inputClass}
                placeholder={mode === 'login' ? 'Your password' : 'At least 8 characters'}
              />
            </div>
            {mode === 'register' && (
              <div>
                <Label className={labelClass}>Confirm password</Label>
                <Input
                  type="password"
                  value={passwordConfirmation}
                  onChange={(e) => onPasswordConfirmationChange(e.target.value)}
                  autoComplete="new-password"
                  className={inputClass}
                  placeholder="Repeat password"
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
